import type { ColumnDef } from '@tanstack/react-table';
import { createSerialColumn, FooterAmount, StackedCellText } from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import { EXPENSE_HEADS } from '../constants/zonalStatement.constants';
import type {
  ExpenseStatementDay,
  ExpenseTypeTotal,
  SpecimenStatementItem,
  SpecimenStatementTotals,
} from '../types/zonalStatement.types';
import { StatementAmountCell } from './StatementAmountCell';

const GRAND_TOTAL_LABEL = 'Grand Total';

export function getExpenseDayColumns(): ColumnDef<ExpenseStatementDay, unknown>[] {
  return [
    createSerialColumn<ExpenseStatementDay>(),
    {
      id: 'date',
      header: 'Date',
      cell: ({ row }) => formatDisplayDate(row.original.date),
    },
    ...EXPENSE_HEADS.map(
      (head): ColumnDef<ExpenseStatementDay, unknown> => ({
        id: head.key,
        header: head.label,
        cell: ({ row }) => <StatementAmountCell value={row.original[head.key]} />,
      }),
    ),
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">{formatNumber(row.original.total)}</span>
      ),
    },
  ];
}

export function getExpenseTypeColumns(): ColumnDef<ExpenseTypeTotal, unknown>[] {
  return [
    createSerialColumn<ExpenseTypeTotal>(),
    { id: 'name', header: 'Expense Type', cell: ({ row }) => row.original.name },
    {
      id: 'claimed',
      header: 'Claimed (Tk)',
      cell: ({ row }) => <StatementAmountCell value={row.original.claimed} />,
    },
    {
      id: 'approved',
      header: 'Approved (Tk)',
      cell: ({ row }) => <StatementAmountCell value={row.original.approved} />,
    },
  ];
}

export function getSpecimenStatementColumns(
  totals: SpecimenStatementTotals | undefined,
): ColumnDef<SpecimenStatementItem, unknown>[] {
  return [
    { ...createSerialColumn<SpecimenStatementItem>(), footer: GRAND_TOTAL_LABEL },
    {
      id: 'name',
      header: 'Specimen',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.name}
          secondary={row.original.code ?? <EmptyValue />}
        />
      ),
    },
    {
      id: 'specimenType',
      header: 'Specimen Type',
      cell: ({ row }) => row.original.bookTypeName ?? <EmptyValue />,
    },
    {
      id: 'netPrice',
      header: 'Net Price',
      cell: ({ row }) => <StatementAmountCell value={row.original.netPrice} />,
    },
    {
      id: 'receivedQty',
      header: 'Received Qty',
      cell: ({ row }) => <StatementAmountCell value={row.original.receivedQty} />,
      footer: () => <FooterAmount amount={totals?.receivedQty} />,
    },
    {
      id: 'distributedQty',
      header: 'Distributed Qty',
      cell: ({ row }) => <StatementAmountCell value={row.original.qty} />,
      footer: () => <FooterAmount amount={totals?.qty} />,
    },
    {
      id: 'inHandQty',
      header: 'In Hand Qty',
      cell: ({ row }) => <StatementAmountCell value={row.original.inHandQty} />,
      footer: () => <FooterAmount amount={totals?.inHandQty} />,
    },
    {
      id: 'inHandAmount',
      header: 'In Hand Amount',
      cell: ({ row }) => <StatementAmountCell value={row.original.inHandAmount} />,
      footer: () => <FooterAmount amount={totals?.inHandAmount} />,
    },
  ];
}
